/**
 * Re-issue error-code / event-token const objects and payload types.
 *
 * The shared package exposes ONLY the codes + window math + availability
 * (framework-free). The NestJS `HttpException` objects that carry these codes
 * live in the backend (`apps/aid-backend/src/modules/cobros/reemision-errors.ts`);
 * this module must never import NestJS.
 */
import type { InvoiceStatus } from './invoice-state-machine';

/** Authoritative re-issue command/preview error tokens (spec error-code table). */
export const REISSUE_ERROR = {
  OUT_OF_WINDOW: 'REISSUE_OUT_OF_WINDOW',
  PACKAGE_LINKED: 'REISSUE_PACKAGE_LINKED',
  STALE_PREVIEW: 'STALE_PREVIEW',
  NO_BILLABLE_LINES: 'NO_BILLABLE_LINES',
} as const;

export type ReissueErrorCode = (typeof REISSUE_ERROR)[keyof typeof REISSUE_ERROR];

/** Payment-annul window constraint token (MODIFIED `payment-annulment`). */
export const PAYMENT_ANNUL_ERROR = {
  OUT_OF_WINDOW: 'PAYMENT_ANNUL_OUT_OF_WINDOW',
} as const;

export type PaymentAnnulErrorCode = (typeof PAYMENT_ANNUL_ERROR)[keyof typeof PAYMENT_ANNUL_ERROR];

/**
 * Re-issue billing-event token. `billing_event.operacion` is free-text with
 * uniqueness scoped to `(operacion, operacion_key)`; there is no exhaustive
 * const of all event tokens (design D3), so this seeds only the re-issue
 * event. Existing literals stay as-is (deferred polish).
 */
export const REISSUE_BILLING_EVENT = {
  INVOICE_REISSUED: 'INVOICE_REISSUED',
} as const;

export type ReissueBillingEvent = (typeof REISSUE_BILLING_EVENT)[keyof typeof REISSUE_BILLING_EVENT];

/**
 * Read-only re-issue preview payload (spec IR-W4, flat interface verbatim).
 * Money fields are `numeric(10,2)` strings; `fingerprint` is attached by the
 * preview service (spec IR-W5) and never computed client-side.
 */
export interface ReemitirPreview {
  idCobro: string;
  idCliente: string;
  periodo: { inicio: string; fin: string };
  diaCobro: number;
  valorOriginal: string;
  nuevoValor: string;
  reservadoPagos: string;
  reservadoCreditos: string;
  /** Credit the replacement can consume: lots already available plus the
   * credit this invoice releases when the annulment returns it to its source
   * lot (revoked adjustment credits excluded). */
  creditoConsumible: string;
  saldoPrevisto: string;
  excedentePrevisto: string;
  fingerprint: string;
}

/** Terminal result of a completed re-issue (new invoice id + inherited state). */
export interface ReemitirCobroResult {
  idCobro: number;
  estado: InvoiceStatus;
  idempotent: boolean;
}
